/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import React, { useState, useEffect, useRef } from 'react';
import { isOnline, subscribe } from '../lib/connectivity';

/**
 * StatoConnessione
 *
 * Banner fisso in cima alla pagina che mostra:
 *  - Nastro arancione quando offline
 *  - Banner verde per 4s quando la connessione è tornata
 *  - Nulla quando online stabile
 *
 * L'operatore in turno (cambio turno offline) è mostrato SOLO nel side menu
 * per non coprire logo e pulsanti in alto.
 *
 * Non richiede props — si aggiorna tramite il modulo `connectivity` (probe
 * attivo di raggiungibilità), non dal solo `navigator.onLine` (segnale debole
 * che lasciava il banner spento col server irraggiungibile ma rete su).
 */
export default function StatoConnessione() {
  const [online, setOnline]   = useState(isOnline());
  const [tornato, setTornato] = useState(false);
  const prevOnline = useRef(isOnline());

  useEffect(() => {
    let timer = null;
    const unsub = subscribe((val) => {
      setOnline(val);
      if (val && !prevOnline.current) {
        // Transizione offline → online: mostra "ripristinata" per 4s.
        setTornato(true);
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => setTornato(false), 4000);
      } else if (!val) {
        setTornato(false);
      }
      prevOnline.current = val;
    });
    return () => { unsub(); if (timer) clearTimeout(timer); };
  }, []);

  // Online stabile → nessun banner
  if (online && !tornato) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        'fixed top-0 left-0 right-0 z-[9999]',
        'flex items-center justify-center gap-2',
        'px-4 py-2 text-sm font-semibold text-white',
        'transition-all duration-300 shadow-md',
        !online ? 'bg-amber-600' : 'bg-emerald-600',
      ].join(' ')}
    >
      {!online ? (
        <>
          <span aria-hidden="true">⚠️</span>
          <span>Modalità offline — I dati visualizzati provengono dalla cache locale</span>
        </>
      ) : (
        <>
          <span aria-hidden="true">✅</span>
          <span>Connessione ripristinata</span>
        </>
      )}
    </div>
  );
}
